import React from 'react';
import type { IconType } from 'react-icons';
import { Box, type BoxProps } from '@chakra-ui/react';
import { iconService, type IconSize, type IconCategory } from '../../services/iconService';

export interface IconProps extends Omit<BoxProps, 'size'> {
  /** Icon name from the icon registry */
  name: string;
  /** Size variant */
  size?: IconSize;
  /** Custom size in pixels (overrides size variant) */
  customSize?: number;
  /** Icon color */
  color?: string;
  /** Additional CSS classes */
  className?: string;
  /** Accessibility label */
  'aria-label'?: string;
  /** Whether icon is decorative (hidden from screen readers) */
  decorative?: boolean;
}

/**
 * Standardized Icon component with consistent sizing and accessibility
 */
export const Icon: React.FC<IconProps> = ({
  name,
  size = 'md',
  customSize,
  color = 'currentColor',
  className,
  'aria-label': ariaLabel,
  decorative = false,
  ...boxProps
}) => {
  const iconComponent = iconService.getIcon(name);
  
  if (!iconComponent) {
    console.warn(`Icon "${name}" not found in registry`);
    return null;
  }

  const iconSize = customSize || iconService.getSize(size);
  
  const IconComponent = iconComponent as IconType;
  
  return (
    <Box
      as="span"
      display="inline-flex"
      alignItems="center"
      justifyContent="center"
      color={color}
      className={className}
      aria-label={decorative ? undefined : ariaLabel}
      aria-hidden={decorative}
      role={decorative ? 'presentation' : 'img'}
      {...boxProps}
    >
      <IconComponent size={iconSize} />
    </Box>
  );
};

/**
 * Icon with predefined size variants for common use cases
 */
export const IconButton: React.FC<IconProps & { onClick?: () => void }> = ({
  onClick,
  ...iconProps
}) => (
  <Icon
    {...iconProps}
    as="button"
    onClick={onClick}
    cursor="pointer"
    _hover={{ opacity: 0.7 }}
    _active={{ opacity: 0.5 }}
    transition="opacity 0.2s"
  />
);

/**
 * Icon with loading state
 */
export const LoadingIcon: React.FC<IconProps & { loading?: boolean }> = ({
  loading = false,
  name = 'refresh',
  ...iconProps
}) => (
  <Icon
    {...iconProps}
    name={name}
    animation={loading ? 'spin 1s linear infinite' : undefined}
    css={{
      '@keyframes spin': {
        '0%': { transform: 'rotate(0deg)' },
        '100%': { transform: 'rotate(360deg)' }
      }
    }}
  />
);

export default Icon;
